import React, { useMemo } from "react";
import GenericCard from "@ui-components/Card/GenericCard";
import { GenericTag } from "@ui-components/Tag/GenericTag";
import { GenericTable, GenericTableColumn } from "@ui-components/Table/GenericTable";
import { KvRow, StatusTag } from "kontrolEdge/components/Sdwan/shared/sdwanFormat";

/**
 * The `vm` block both managed services answer with (P5): Virtual Firewall fills it from
 * FortiManager (CTX-730), the SD-WAN hub from InfluxDB/Proxmox (CTX-731). One shape, one
 * card — a field one service gains exists, as `null`, for the other too.
 *
 * A field without a source travels `null`, never `0` or `""` (intent I4).
 */
export interface VmSpecs {
    vcpu: number | null;
    memory_mb: number | null;
    firmware: string | null;
    /** Calendar date, `YYYY-MM-DD` — not an instant. */
    license_expires_on: string | null;
    platform: string | null;
    /**
     * Still sent by the backend, no longer shown: for most firewalls it was always "—",
     * and the hub repeated its datacenter. The overview redesign (P-CTX-95, milestone 2)
     * decides where location lives.
     */
    hosting: string | null;
}

/**
 * One member of an HA cluster. FortiManager reports the pair as a single device.
 * No name and no host: both are internal infrastructure, and the backend does
 * not send them (CTX-953).
 */
export interface HaMember {
    serial: string | null;
    role: "primary" | "secondary";
    status: "up" | "down";
}

const EMPTY = "—";

const valueOr = (value: string | number | null) => (value == null ? EMPTY : String(value));

function formatMemory(mb: number | null): string {
    if (mb == null) return EMPTY;
    return mb >= 1024 ? `${(mb / 1024).toFixed(1)} GB` : `${mb} MB`;
}

/**
 * A calendar date (`YYYY-MM-DD`, or an ISO instant at midnight UTC) read in UTC: in the
 * browser's zone, midnight UTC is still the previous day in Mexico (UTC-6).
 */
export function formatCalendarDate(date: string | null | undefined): string {
    if (!date) return EMPTY;
    const parsed = new Date(/^\d{4}-\d{2}-\d{2}$/.test(date) ? `${date}T00:00:00Z` : date);
    if (Number.isNaN(parsed.getTime())) return date;
    return parsed.toLocaleDateString("en-US", {
        timeZone: "UTC",
        year: "numeric",
        month: "short",
        day: "numeric",
    });
}

const HA_COLUMNS: GenericTableColumn<HaMember>[] = [
    { header: "Serial number", renderCell: (m) => valueOr(m.serial) },
    {
        header: "Role",
        renderCell: (m) => (
            <GenericTag
                variant={m.role === "primary" ? "success" : "default"}
                label={m.role === "primary" ? "Primary" : "Secondary"}
                size="small"
            />
        ),
    },
    { header: "Status", renderCell: (m) => <StatusTag status={m.status} /> },
];

/**
 * The members of an HA cluster, as a full-width table under the detail cards: it has
 * too many columns to share a row with them (CTX-732). Draws nothing for a standalone
 * VM — only a cluster has members. The SD-WAN hub never passes any.
 */
export function HaMembersCard({ members }: { members: HaMember[] }) {
    if (members.length === 0) return null;
    return (
        <GenericCard isCollapsible={false} variant="primary" shadow="sm">
            <div className="p-5">
                <h3 className="font-semibold">High availability</h3>
                <p className="text-sm text-semantic-text-secondary mb-3">
                    Members of the cluster these specs describe
                </p>
                <GenericTable
                    columns={HA_COLUMNS}
                    data={members}
                    width="100%"
                    tableClassName="bg-semantic-bg-primary"
                />
            </div>
        </GenericCard>
    );
}

/**
 * Specs of the virtual machine behind a managed service. Every row is always drawn — a
 * missing value shows `—`, because that the data is missing is itself information.
 */
export default function VmSpecsCard({ vm }: { vm: VmSpecs | null }) {
    const rows = useMemo(
        () => [
            { label: "vCPU", value: valueOr(vm?.vcpu ?? null) },
            { label: "Memory", value: formatMemory(vm?.memory_mb ?? null) },
            { label: "Firmware", value: valueOr(vm?.firmware ?? null) },
            { label: "License expires", value: formatCalendarDate(vm?.license_expires_on ?? null) },
            { label: "Platform", value: valueOr(vm?.platform ?? null) },
        ],
        [vm]
    );

    return (
        <GenericCard isCollapsible={false} variant="primary" shadow="sm">
            <div className="p-5">
                <h3 className="font-semibold mb-3">Virtual machine</h3>
                {vm == null && (
                    <p className="text-sm text-semantic-text-secondary mb-2">
                        VM specs are unavailable right now.
                    </p>
                )}
                {rows.map((row) => (
                    <KvRow key={row.label} label={row.label}>
                        {row.value}
                    </KvRow>
                ))}
            </div>
        </GenericCard>
    );
}
